/**
 * What the review calls itself, and where it says the reviewer is.
 *
 * "Review Manifest Document Data" described what the screen is for, which the
 * reviewer already knows — they opened it. What they cannot read off the form is
 * *which* manifest they are on and whose it is, and on a queue that crosses
 * tenants that is the part worth saying.
 */
export interface ManifestReviewHeader {
  title: string;
  /** Empty when nothing is known yet, so a caller can leave the slot out. */
  subheader: string;
}

export function manifestReviewHeader({
  trackingNumber,
  tenantName,
  facilityName,
  index,
  total,
}: {
  trackingNumber: string | null | undefined;
  tenantName: string | null | undefined;
  facilityName: string | null | undefined;
  /** Zero-based position in the batch. */
  index: number;
  total: number;
}): ManifestReviewHeader {
  const named = trackingNumber != null && trackingNumber.trim().length > 0;

  return {
    /**
     * The manifest, named — no verb.
     *
     * This used to open with one ("Enter manifest 0265…"), which was the third
     * thing on screen saying what state the document was in: a "Needs review"
     * chip sits beside the title and the primary button reads "Mark Reviewed".
     * A title earns its place by identifying the thing, and the tracking number
     * is what the reviewer is holding a piece of paper to compare against.
     *
     * Falls back while the number is still loading, so the header is never the
     * word "Manifest" on its own.
     */
    title: named ? `Manifest ${trackingNumber.trim()}` : "Manifest document",
    // Each part is dropped when unknown rather than left as a gap: a manifest
    // with no facility yet reads as "Document 3 of 12", not " ·  · Document 3
    // of 12". The org leads, because a facility name alone does not say whose.
    // Capitalised like the names beside it — these are items in a list, not a
    // sentence, and a lone lowercase tail reads as a mistake.
    subheader: [tenantName, facilityName, total > 1 ? `Document ${index + 1} of ${total}` : null]
      .filter((part): part is string => part != null && part.trim().length > 0)
      .join(" · "),
  };
}
